import { CueKind, TurnInstruction, cumulativeDistancesMeters } from './turnInstructions';

/**
 * What a hand-added cue can be.
 *
 * Every turn here is a sign the guidance runtime already speaks, draws and localises, so a
 * cue is indistinguishable from one GraphHopper produced — which is the point. `note` is the
 * exception: it has no direction, so it carries a "continue" sign purely to travel through a
 * pipeline that positions everything by turn, and is marked as a note so nothing draws an
 * arrow for it.
 *
 * `uturn` stores as the left-hand variant. GraphHopper distinguishes the two by which way the
 * road forces you round; on foot there is no such constraint, so offering both would be a
 * distinction without a difference.
 *
 * The labels and signs match `PlannerCueType` on Android and iOS exactly. A cue written on
 * one platform is read on the other two, so a divergence here is a divergence in what a
 * hiker is told.
 */
export interface PlannerCueType {
  id: string;
  label: string;
  sign: number;
  kind: CueKind;
}

export const PLANNER_CUE_TYPES: PlannerCueType[] = [
  { id: 'note', label: 'General info', sign: 0, kind: 'note' },
  { id: 'left', label: 'Turn left', sign: -2, kind: 'turn' },
  { id: 'right', label: 'Turn right', sign: 2, kind: 'turn' },
  { id: 'straight', label: 'Continue straight', sign: 0, kind: 'turn' },
  { id: 'slight_left', label: 'Bear left', sign: -1, kind: 'turn' },
  { id: 'slight_right', label: 'Bear right', sign: 1, kind: 'turn' },
  { id: 'sharp_left', label: 'Sharp left', sign: -3, kind: 'turn' },
  { id: 'sharp_right', label: 'Sharp right', sign: 3, kind: 'turn' },
  { id: 'keep_left', label: 'Keep left', sign: -7, kind: 'turn' },
  { id: 'keep_right', label: 'Keep right', sign: 7, kind: 'turn' },
  { id: 'uturn', label: 'Turn around', sign: -8, kind: 'turn' }
];

export function cueTypeById(id: string): PlannerCueType | undefined {
  return PLANNER_CUE_TYPES.find((type) => type.id === id);
}

/** GraphHopper's closing signs: an arrival, or a via point reached. */
function isTerminal(sign: number): boolean {
  return sign === 4 || sign === 5;
}

/**
 * The turns for a segment with a cue added at `distanceAlongMeters`.
 *
 * A cue has to look enough like a GraphHopper turn to survive the recording apps' parser,
 * which discards entries on rules that have nothing to do with their content:
 *
 *  - the entry at **list index 0** of any segment but the first is dropped, because that is
 *    where GraphHopper's per-segment "continue" sits and the route only has one real start;
 *  - anything **after the closing arrival** demotes that arrival out of last place, and the
 *    route loses its finish;
 *  - every distance is **rescaled** by the ratio between the segment's geometry and the
 *    length the capture measured, which disagree by a few percent.
 *
 * So the position in the list matters even though the parser sorts by distance afterwards,
 * and a distance measured on the geometry is not the number to store. Getting either wrong
 * fails silently — the cue is simply absent — which is why this is a pure function with its
 * own tests rather than logic inside a dialog.
 *
 * Mirrors `PlannerCueWriter` on Android and iOS.
 */
export function withCue(
  existing: TurnInstruction[],
  coordinates: number[][],
  options: { distanceAlongMeters: number; sign: number; text: string; kind: CueKind }
): TurnInstruction[] {
  const distances = cumulativeDistancesMeters(coordinates);
  const geometryLength = distances.length > 0 ? distances[distances.length - 1] : 0;
  if (geometryLength <= 0) return existing;

  const current = existing ?? [];
  const capturedLength = lastTerminalDistance(current);

  const cue: TurnInstruction = {
    d: clamp(
      capturedDistance(options.distanceAlongMeters, capturedLength, geometryLength),
      0,
      capturedLength ?? geometryLength
    ),
    sign: options.sign,
    // A person's words go in `utext`, whether they added the entry or re-worded one
    // GraphHopper wrote. `cue` is left meaning only where the entry came from, so nothing
    // downstream has to know the difference to word it correctly — and both phones write the
    // same shape, so a cue reads the same whichever app made it.
    text: '',
    utext: options.text,
    cue: options.kind
  };

  return insert(cue, current, geometryLength);
}

function lastTerminalDistance(instructions: TurnInstruction[]): number | null {
  for (let i = instructions.length - 1; i >= 0; i--) {
    if (isTerminal(instructions[i].sign)) return instructions[i].d;
  }
  return null;
}

/**
 * The distance on the scale the stored turns are measured in.
 *
 * Capture rounds coordinates before measuring, so its lengths run between about 1% short and
 * 5% long against the geometry — and the parser divides that back out. A cue measured on the
 * geometry and stored unconverted is therefore multiplied by a ratio it was never in,
 * landing metres from where the user clicked.
 */
function capturedDistance(
  geometryDistance: number,
  capturedLength: number | null,
  geometryLength: number
): number {
  if (capturedLength === null || capturedLength <= 0) return geometryDistance;
  return (geometryDistance * capturedLength) / geometryLength;
}

/**
 * Put the cue where the parser will keep it.
 *
 * Before any closing arrival, so the arrival stays last; never at index 0, which is reserved
 * for the opening "continue" the parser strips from every later segment. A segment with
 * nothing in it — a straight line the user drew by hand — is given both bookends, so it
 * reads like a routed one and its distances are left alone.
 */
function insert(
  cue: TurnInstruction,
  into: TurnInstruction[],
  geometryLength: number
): TurnInstruction[] {
  const opening: TurnInstruction = { d: 0, sign: 0, text: '' };
  const arrival: TurnInstruction = { d: geometryLength, sign: 4, text: '' };

  if (into.length === 0) return [opening, cue, arrival];

  const result = [...into];
  const terminalIndex = result.findIndex((entry) => isTerminal(entry.sign));
  result.splice(terminalIndex >= 0 ? terminalIndex : result.length, 0, cue);

  // Only reachable for a segment whose single entry was its arrival; without an opening entry
  // ahead of it the cue would be stripped as this segment's own "continue".
  if (result[0] === cue) result.unshift(opening);
  return result;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * The turns a segment should carry with the entry at [entryIndex] re-worded, re-signed, or
 * both.
 *
 * GraphHopper's own `text` and `sign` are never written over — a person's choices go in
 * `utext` and `usign` beside them. So clearing a wording is deleting a key rather than
 * restoring a sentence from somewhere, and a re-snap that rewrites the turn keeps their words
 * on it. Both phones write the same shape.
 */
export function withEditedTurn(
  turns: TurnInstruction[],
  entryIndex: number,
  edit: { text?: string; sign?: number }
): TurnInstruction[] {
  const target = turns[entryIndex];
  if (!target) return turns;

  const text = edit.text?.trim();
  const result = [...turns];
  result[entryIndex] = {
    ...target,
    utext: text ? text : undefined,
    // Storing the original sign as an override would be a lie about what was chosen, and
    // would make "has this been edited?" unanswerable.
    usign: edit.sign === undefined || edit.sign === target.sign ? undefined : edit.sign
  };
  return result;
}

/**
 * The turns a segment should carry with the entry at [entryIndex] taken out of the list.
 *
 * What "taken out" means depends on who wrote it. A cue is the person's own and nothing
 * regenerates it, so it goes. A routed turn is GraphHopper's description of the geometry and
 * comes back with the next re-snap, so removing it would look like the delete had failed — it
 * is flagged `hidden` instead, which the reader filters after the seam detector has seen it.
 */
export function withDeletedTurn(turns: TurnInstruction[], entryIndex: number): TurnInstruction[] {
  const target = turns[entryIndex];
  if (!target) return turns;

  if (target.cue !== undefined) return turns.filter((_, index) => index !== entryIndex);

  const result = [...turns];
  result[entryIndex] = { ...target, hidden: true };
  return result;
}

/**
 * The cue type a stored turn should reopen the dialog on.
 *
 * `kind` decides first, because `note` and `straight` share a `continue` sign and only the
 * stored kind tells them apart. A sign with no type of its own — a roundabout, a right-hand
 * U-turn — has no entry to select, so it falls back to the note rather than silently offering
 * a direction nobody chose.
 */
export function cueTypeForSign(sign: number, isNote: boolean): PlannerCueType {
  const note = PLANNER_CUE_TYPES.find((type) => type.kind === 'note')!;
  if (isNote) return note;
  return PLANNER_CUE_TYPES.find((type) => type.kind === 'turn' && type.sign === sign) ?? note;
}
